"use client"

import React, { useRef } from "react"
import { Provider } from "react-redux"
import {
    type AppStore,
    baseApi,
    clearActiveOrgGroup,
    makeStore,
    PersistedSessionLoadState,
    setActiveIds,
    setPersistedSessionLoadState,
    setUser,
    useAppDispatch
} from "services"
import { clearPersistedUser } from "services/src/state/user/persistedUserSlice"
import {
    setPersistedBMSubscriptions,
    setPersistedMMBBrands,
    setPersistedPPSubscriptions
} from "services/src/state/user/persistedUserSubscriptionsSlice"
import type { PerformancePlatformSubscription, User } from "types"

export interface StateProviderProps {
    children: React.ReactNode
    userInformation: {
        user: User
        activeIds: {
            bma: string
            brand: string
            pp: string
            brewknowledge: string
        }
        userSubscriptions: {
            ppSubscriptions: User["ppSubscriptions"]
            bmSubscriptions: User["bmSubscriptions"]
            mmbBrands: User["mmbBrands"]
        }
        subscriptions: {
            ppSubscriptions: PerformancePlatformSubscription[]
        }
    }
}

export default function StoreProvider({ children, userInformation }: StateProviderProps) {
    const storeRef = useRef<AppStore | null>(null)
    // const { user, activeIds, userSubscriptions } = userInformation
    const user = userInformation?.user
    const activeIds = userInformation?.activeIds
    const userSubscriptions = userInformation?.userSubscriptions
    const dispatch = useAppDispatch()
    if (!storeRef.current) {
        storeRef.current = makeStore()
    }

    const isBlxUser = user?.trustedUser || user?.contact?.email.includes("@brewlogix.com")

    React.useEffect(() => {
        if (userInformation) {
            if (
                storeRef.current.getState()["persisted-user"].activeIds.pp.length < 1 ||
                storeRef.current.getState()["persisted-user"].user.id !== userInformation.user.id
            ) {
                // LEAVE FOR DEBUGGING
                // console.log(
                //     'storeRef.current is null',
                //     storeRef.current.getState()['persisted-user'].activeIds,
                //     storeRef.current.getState()['persisted-user'].user.id !== userInformation.user.id,
                //     userInformation.user.id
                // )
                storeRef.current.dispatch(setUser(user))
                storeRef.current.dispatch(setActiveIds(activeIds))
                storeRef.current.dispatch(
                    setPersistedPPSubscriptions(userSubscriptions.ppSubscriptions)
                )
                storeRef.current.dispatch(
                    setPersistedBMSubscriptions(userSubscriptions.bmSubscriptions)
                )
                storeRef.current.dispatch(setPersistedMMBBrands(userSubscriptions.mmbBrands))
                storeRef.current.dispatch(
                    setPersistedSessionLoadState(PersistedSessionLoadState.Loaded)
                )
            }
        }
        return () => {
            dispatch(clearPersistedUser())
            dispatch(baseApi.util.resetApiState())
            dispatch(clearActiveOrgGroup())
        }
    }, [userInformation])

    return <Provider store={storeRef.current}>{children}</Provider>
}
